/**
 * Remote logger utility that forwards console logs to the terminal during development
 * Used primarily for mobile testing to see logs in the dev terminal
 *
 * TOGGLE CONTROL:
 * - Frontend: Only active in DEV and QA environments
 * - Backend: Controlled by application.remote-logging.enabled in application.yaml
 *   - DEV: enabled=true (logs to localhost terminal)
 *   - QA/PROD: enabled=false (no Google Cloud Console spam)
 *
 * Lines are queued and flushed as ONE batched POST per flush window — a console-heavy
 * moment can never storm the network again (net::ERR_INSUFFICIENT_RESOURCES).
 */

const FLUSH_MS = 2000;
const MAX_LINE_CHARS = 500;
const MAX_BATCH_LINES = 200;

type RemoteLogEntry = { level: string; message: string };

export const setupRemoteLogger = () => {
  // Enable in development AND QA
  if (!import.meta.env.DEV && import.meta.env.VITE_ENV !== 'qa') {
    return;
  }

  // Store original console methods
  const originalConsole = { ...console };

  let queue: RemoteLogEntry[] = [];
  let dropped = 0;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  const safeStringify = (value: any): string => {
    try { return JSON.stringify(value); } catch { return String(value); }
  };

  const flush = () => {
    flushTimer = null;
    if (queue.length === 0) return;

    const logs = queue;
    queue = [];
    if (dropped > 0) {
      logs.push({ level: 'warn', message: `(+${dropped} log lines dropped by remoteLogger batch cap)` });
      dropped = 0;
    }

    fetch('/api/crm/__log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs }),
      keepalive: true
    }).catch(() => {});
  };

  const enqueue = (level: string, args: any[]) => {
    // Skip if this is already a Logger-processed message
    if (typeof args[0] !== 'string' || args[0].includes('[Client]')) return;

    if (queue.length >= MAX_BATCH_LINES) {
      dropped++;
      return;
    }

    const message = args.map(a => (typeof a === 'string' ? a : safeStringify(a))).join(' ');
    queue.push({
      level,
      message: message.length > MAX_LINE_CHARS ? `${message.slice(0, MAX_LINE_CHARS)}…` : message
    });

    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
  };

  // Override console methods to queue logs for the batched flush
  console.log = (...args) => { originalConsole.log(...args); enqueue('log', args); };
  console.info = (...args) => { originalConsole.info(...args); enqueue('info', args); };
  console.warn = (...args) => { originalConsole.warn(...args); enqueue('warn', args); };
  console.error = (...args) => { originalConsole.error(...args); enqueue('error', args); };
};

export default setupRemoteLogger;
